import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { useAuth } from '../context/AuthContext';

const HotelManagement = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [searchLocation, setSearchLocation] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    stars: '3',
    location: '',
    destination: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    latitude: '',
    longitude: '',
    amenities: '',
    mealPlans: [],
    roomTypes: [],
    description: '',
    contactNumber: '',
    email: '',
    website: '',
    images: []
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchHotels();
  }, []);

  const fetchHotels = async () => {
    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('companyToken') || 
                    localStorage.getItem('employeeToken');
      
      const response = await axios.get('/master-data/hotels', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHotels(response.data.hotels || response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching hotels:', error);
      setLoading(false);
    }
  };

  const handleSearchLocation = () => {
    if (!searchLocation) {
      alert('Please enter a location to search');
      return;
    }

    // Open Google Maps in new tab for user to find location
    const mapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(searchLocation)}`;
    window.open(mapsUrl, '_blank');
    
    alert('📍 Find your hotel on Google Maps, then:\n1. Click on the hotel\n2. Copy the coordinates from URL\n3. Paste them in Latitude/Longitude fields below');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('companyToken') || 
                    localStorage.getItem('employeeToken');

      const hotelData = {
        ...formData,
        stars: parseInt(formData.stars),
        amenities: formData.amenities.split(',').map(a => a.trim()).filter(a => a),
        mealPlans: formData.mealPlans,
        roomTypes: formData.roomTypes,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      if (editingHotel) {
        await axios.put(
          `/master-data/hotels/${editingHotel._id}`,
          hotelData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        alert('✅ Hotel updated successfully!');
      } else {
        await axios.post(
          '/master-data/hotels',
          hotelData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        alert('✅ Hotel added successfully!');
      }

      resetForm();
      fetchHotels();
      setShowForm(false);
    } catch (error) {
      console.error('Error saving hotel:', error);
      alert('❌ Error saving hotel: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);
    
    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('companyToken') || 
                    localStorage.getItem('employeeToken');
      
      const uploadedImages = [];
      
      for (const file of files) {
        const formDataImg = new FormData();
        formDataImg.append('image', file);
        formDataImg.append('folder', 'hotels');
        
        const response = await axios.post(
          '/upload/image',
          formDataImg,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        uploadedImages.push({
          url: response.data.url,
          publicId: response.data.publicId,
          caption: ''
        });
      }
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
      
      alert(`✅ ${uploadedImages.length} image(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('❌ Error uploading images: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleEdit = (hotel) => {
    setEditingHotel(hotel);
    setFormData({
      name: hotel.name || '',
      stars: hotel.stars?.toString() || '3',
      location: hotel.location || '',
      destination: hotel.destination || '',
      address: hotel.address || '',
      city: hotel.city || '',
      state: hotel.state || '',
      country: hotel.country || 'India',
      latitude: hotel.latitude?.toString() || '',
      longitude: hotel.longitude?.toString() || '',
      amenities: hotel.amenities?.join(', ') || '',
      mealPlans: hotel.mealPlans || [],
      roomTypes: hotel.roomTypes || [],
      description: hotel.description || '',
      contactNumber: hotel.contactNumber || '',
      email: hotel.email || '',
      website: hotel.website || '',
      images: hotel.images || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hotel?')) return;

    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('companyToken') || 
                    localStorage.getItem('employeeToken');

      await axios.delete(`/master-data/hotels/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Hotel deleted successfully!');
      fetchHotels();
    } catch (error) {
      console.error('Error deleting hotel:', error);
      alert('❌ Error deleting hotel');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      stars: '3',
      location: '',
      destination: '',
      address: '',
      city: '',
      state: '',
      country: 'India',
      latitude: '',
      longitude: '',
      amenities: '',
      mealPlans: [],
      roomTypes: [],
      description: '',
      contactNumber: '',
      email: '',
      website: '',
      images: []
    });
    setEditingHotel(null);
    setSearchLocation('');
  };

  const handleMealPlanToggle = (mealPlan) => {
    if (formData.mealPlans.includes(mealPlan)) {
      setFormData({
        ...formData,
        mealPlans: formData.mealPlans.filter(m => m !== mealPlan)
      });
    } else {
      setFormData({
        ...formData,
        mealPlans: [...formData.mealPlans, mealPlan]
      });
    }
  };

  const handleRoomTypeToggle = (roomType) => {
    if (formData.roomTypes.includes(roomType)) {
      setFormData({
        ...formData,
        roomTypes: formData.roomTypes.filter(r => r !== roomType)
      });
    } else {
      setFormData({
        ...formData,
        roomTypes: [...formData.roomTypes, roomType]
      });
    }
  };

  const openInGoogleMaps = (lat, lng, name) => {
    if (lat && lng) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
    } else if (name) {
      window.open(`https://www.google.com/maps/search/${encodeURIComponent(name)}`, '_blank');
    }
  };

  if (loading) return <div className="p-8">Loading hotels...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Hotel Management</h2>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          ➕ Add New Hotel
        </button>
      </div>

      {!showForm ? (
        <div className="space-y-4">
          {/* Hotels List */}
          {hotels.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-500 mb-4">No hotels added yet</p>
              <button
                onClick={() => setShowForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Your First Hotel
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {hotels.map(hotel => (
                <div key={hotel._id} className="bg-white border rounded-lg overflow-hidden shadow hover:shadow-lg transition">
                  {/* Hotel Image */}
                  {hotel.images && hotel.images.length > 0 ? (
                    <div className="relative h-48 bg-gray-200">
                      <img
                        src={hotel.images[0].url}
                        alt={hotel.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100"><span class="text-6xl">🏨</span></div>';
                        }}
                      />
                      {hotel.images.length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                          +{hotel.images.length - 1} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                      <span className="text-6xl">🏨</span>
                    </div>
                  )}
                  
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-bold text-gray-800">{hotel.name}</h3>
                      <div className="flex gap-1">
                        {[...Array(hotel.stars || 3)].map((_, i) => (
                          <span key={i} className="text-yellow-500">⭐</span>
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-1">📍 {hotel.destination || hotel.city}</p>
                    <p className="text-xs text-gray-500 mb-2">{hotel.location || hotel.address}</p>
                  
                  {hotel.mealPlans && hotel.mealPlans.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-500 mb-1">🍽️ Meal Plans:</p>
                      <div className="flex flex-wrap gap-1">
                        {hotel.mealPlans.map((meal, idx) => (
                          <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                            {meal}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {hotel.roomTypes && hotel.roomTypes.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-500 mb-1">🛏️ Room Types:</p>
                      <div className="flex flex-wrap gap-1">
                        {hotel.roomTypes.slice(0, 2).map((room, idx) => (
                          <span key={idx} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            {room}
                          </span>
                        ))}
                        {hotel.roomTypes.length > 2 && (
                          <span className="text-xs text-gray-500">+{hotel.roomTypes.length - 2}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {hotel.amenities && hotel.amenities.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Amenities:</p>
                      <div className="flex flex-wrap gap-1">
                        {hotel.amenities.slice(0, 3).map((amenity, idx) => (
                          <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            {amenity}
                          </span>
                        ))}
                        {hotel.amenities.length > 3 && (
                          <span className="text-xs text-gray-500">+{hotel.amenities.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-3">
                    {(hotel.latitude && hotel.longitude) && (
                      <button
                        onClick={() => openInGoogleMaps(hotel.latitude, hotel.longitude, hotel.name)}
                        className="flex-1 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                      >
                        🗺️ Map
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(hotel)}
                      className="flex-1 px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(hotel._id)}
                      className="flex-1 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">
              {editingHotel ? 'Edit Hotel' : 'Add New Hotel'}
            </h3>
            <button
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕ Close
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="border-b pb-4">
              <h4 className="font-semibold mb-3">Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Hotel Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g., Hotel Paradise"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Star Rating *</label>
                  <select
                    required
                    value={formData.stars}
                    onChange={(e) => setFormData({ ...formData, stars: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="1">1 Star ⭐</option>
                    <option value="2">2 Star ⭐⭐</option>
                    <option value="3">3 Star ⭐⭐⭐</option>
                    <option value="4">4 Star ⭐⭐⭐⭐</option>
                    <option value="5">5 Star ⭐⭐⭐⭐⭐</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Location Info */}
            <div className="border-b pb-4">
              <h4 className="font-semibold mb-3">Location Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Destination/Trip *</label>
                  <input
                    type="text"
                    required
                    value={formData.destination}
                    onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g., Goa, Kerala, Rajasthan"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 This will be used to filter hotels in quotations
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Location/Area</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g., North Goa, Calangute"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g., Panaji"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g., Goa"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Full Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows="2"
                  placeholder="Complete address..."
                />
              </div>
            </div>

            {/* Google Maps Integration */}
            <div className="border-b pb-4 bg-blue-50 p-4 rounded">
              <h4 className="font-semibold mb-3">📍 Google Maps Location</h4>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Search Hotel on Google Maps</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                    className="flex-1 border rounded px-3 py-2"
                    placeholder="Enter hotel name or address"
                  />
                  <button
                    type="button"
                    onClick={handleSearchLocation}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    🔍 Search on Maps
                  </button>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  💡 Click search, find hotel on Google Maps, copy coordinates from URL
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Latitude</label>
                  <input
                    type="text"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g., 15.2993"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Longitude</label>
                  <input
                    type="text"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g., 74.1240"
                  />
                </div>
              </div>
              {formData.latitude && formData.longitude && (
                <button
                  type="button"
                  onClick={() => openInGoogleMaps(formData.latitude, formData.longitude, formData.name)}
                  className="mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                >
                  🗺️ View on Google Maps
                </button>
              )}
            </div>

            {/* Meal Plans */}
            <div className="border-b pb-4">
              <h4 className="font-semibold mb-3">🍽️ Meal Plans Available</h4>
              <p className="text-xs text-gray-500 mb-3">Select all meal plans offered by this hotel</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['EP (Room Only)', 'CP (Bed & Breakfast)', 'MAP (Half Board)', 'AP (Full Board)', 'AI (All Inclusive)'].map((meal) => (
                  <label key={meal} className="flex items-center gap-2 p-3 border rounded cursor-pointer hover:bg-green-50">
                    <input
                      type="checkbox"
                      checked={formData.mealPlans.includes(meal)}
                      onChange={() => handleMealPlanToggle(meal)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{meal}</span>
                  </label>
                ))}
              </div>
              {formData.mealPlans.length > 0 && (
                <div className="mt-2 p-2 bg-green-50 rounded">
                  <p className="text-xs text-green-700">
                    ✓ {formData.mealPlans.length} meal plan(s) selected
                  </p>
                </div>
              )}
            </div>

            {/* Room Types */}
            <div className="border-b pb-4">
              <h4 className="font-semibold mb-3">🛏️ Room Types Available</h4>
              <p className="text-xs text-gray-500 mb-3">Select all room types available in this hotel</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['Standard Room', 'Deluxe Room', 'Super Deluxe', 'Suite', 'Executive Suite', 'Presidential Suite', 'Family Room', 'Twin Room', 'Single Room'].map((room) => (
                  <label key={room} className="flex items-center gap-2 p-3 border rounded cursor-pointer hover:bg-purple-50">
                    <input
                      type="checkbox"
                      checked={formData.roomTypes.includes(room)}
                      onChange={() => handleRoomTypeToggle(room)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{room}</span>
                  </label>
                ))}
              </div>
              {formData.roomTypes.length > 0 && (
                <div className="mt-2 p-2 bg-purple-50 rounded">
                  <p className="text-xs text-purple-700">
                    ✓ {formData.roomTypes.length} room type(s) selected
                  </p>
                </div>
              )}
            </div>

            {/* Amenities */}
            <div className="border-b pb-4">
              <h4 className="font-semibold mb-3">Amenities & Facilities</h4>
              <div>
                <label className="block text-sm font-medium mb-1">Amenities (comma-separated)</label>
                <textarea
                  value={formData.amenities}
                  onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows="3"
                  placeholder="e.g., WiFi, Swimming Pool, Restaurant, Spa, Gym, Parking"
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows="3"
                  placeholder="Brief description of the hotel..."
                />
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold mb-3">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Number</label>
                  <input
                    type="tel"
                    value={formData.contactNumber}
                    onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="+91 1234567890"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="hotel@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="https://hotel.com"
                  />
                </div>
              </div>
            </div>

            {/* Hotel Images */}
            <div>
              <h4 className="font-semibold mb-3">🖼️ Hotel Images</h4>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  <input
                    type="file"
                    id="hotel-images"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                  <label
                    htmlFor="hotel-images"
                    className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 cursor-pointer transition-all shadow-md ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {uploadingImage ? 'Uploading...' : 'Upload Hotel Images'}
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    Upload multiple images (Exterior, Rooms, Amenities, etc.)
                  </p>
                </div>

                {/* Image Preview Grid */}
                {formData.images && formData.images.length > 0 && (
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image.url}
                          alt={`Hotel ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove image"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center rounded-b-lg">
                          Image {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="px-6 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                {editingHotel ? '💾 Update Hotel' : '✅ Add Hotel'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default HotelManagement;
